import axios from "axios";

const BASE_URL = (
  process.env.REACT_APP_BASE_URL ||
  "https://services.allhomedesire.com"
).replace(/\/+$/, "");

/**
 * Helper to get authentication headers from token
 */
export const getAuthHeaders = (token) => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

/* =========================================================================
   1. KHATABOOK TOOL APIS
   ========================================================================= */

/**
 * 1.1. Get All Khatabooks
 * GET /api/khatabook/list/all
 */
export const getAllKhatabooks = async (token) => {
  const response = await axios.get(`${BASE_URL}/api/khatabook/list/all`, {
    headers: getAuthHeaders(token),
  });
  return response.data;
};

/**
 * 1.2. Create the user's Khatabooks
 * POST /api/khatabook/create (no body)
 * The backend creates the HOME and OFFICE books once per user; calling it again changes nothing.
 */
export const createKhatabook = async (token) => {
  const response = await axios.post(
    `${BASE_URL}/api/khatabook/create`,
    {},
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 1.3. Get Khatabook Summary (by Khatabook ID)
 * GET /api/khatabook/{khatabookId}/summary
 */
export const getKhatabookSummary = async (token, khatabookId) => {
  const response = await axios.get(
    `${BASE_URL}/api/khatabook/${khatabookId}/summary`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 1.4. List Customers in Khatabook
 * POST /api/khatabook/{khatabookId}/customers/list
 * Body: { "page_number": 1, "page_size": 1000, "order_by": "DESC" }
 * Response: { khataBookCustomers: [{ id, customer_name, phone, totalBalance }], totalPage, totalRecord }
 */
export const listKhatabookCustomers = async (
  token,
  khatabookId,
  pageNumber = 1,
  pageSize = 1000
) => {
  const response = await axios.post(
    `${BASE_URL}/api/khatabook/${khatabookId}/customers/list`,
    {
      page_number: pageNumber,
      page_size: pageSize,
      order_by: "DESC",
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 1.5. Add Customer to Khatabook
 * POST /api/khatabook/{khatabookId}/customer
 * Body: { "name": "John Doe", "phone": "9876543210" }
 */
export const addKhatabookCustomer = async (
  token,
  khatabookId,
  { name, phone }
) => {
  const response = await axios.post(
    `${BASE_URL}/api/khatabook/${khatabookId}/customer`,
    {
      name: String(name).trim(),
      phone: String(phone).trim(),
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 1.6. Edit Khatabook Customer
 * PUT /api/khatabook/customer/{customerId}
 * Body: { "name": "John Doe Updated", "phone": "9876543210" }
 */
export const editKhatabookCustomer = async (
  token,
  customerId,
  { name, phone }
) => {
  const response = await axios.put(
    `${BASE_URL}/api/khatabook/customer/${customerId}`,
    {
      name: String(name).trim(),
      phone: String(phone).trim(),
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 1.7. Delete Khatabook Customer
 * DELETE /api/khatabook/customers/{customerId}
 */
export const deleteKhatabookCustomer = async (token, customerId) => {
  const response = await axios.delete(
    `${BASE_URL}/api/khatabook/customers/${customerId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 1.8. Get Customer Summary (Transactions Overview)
 * GET /api/khatabook/customers/{customerId}/summary
 */
export const getCustomerSummary = async (token, customerId) => {
  const response = await axios.get(
    `${BASE_URL}/api/khatabook/customers/${customerId}/summary`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 1.9. Search / List Customer Transactions
 * POST /api/khatabook/customer/{customerId}/transactions/search
 * Body: { "page_number": 1, "page_size": 100, "order_by": "DESC" }
 * Response: { khataTransactions: [{ id, amount, type, mode, note, transactionDate }] }
 */
export const searchCustomerTransactions = async (
  token,
  customerId,
  pageNumber = 1,
  pageSize = 100
) => {
  const response = await axios.post(
    `${BASE_URL}/api/khatabook/customer/${customerId}/transactions/search`,
    {
      page_number: pageNumber,
      page_size: pageSize,
      order_by: "DESC",
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 1.10. Add Transaction to Customer
 * POST /api/khatabook/customer/{customerId}/transaction
 * Body: { "amount": 5000, "type": "CREDIT" | "DEBIT", "note": "...",
 *         "transactionDate": "2026-01-31T10:30:00", "khataBookMode": "CASH" | "ONLINE" | "UPI" }
 */
export const addCustomerTransaction = async (
  token,
  customerId,
  { amount, type, note, transactionDate, khataBookMode }
) => {
  const response = await axios.post(
    `${BASE_URL}/api/khatabook/customer/${customerId}/transaction`,
    {
      amount: Number(amount),
      type,
      note: note ? String(note).trim() : "",
      transactionDate,
      khataBookMode,
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 1.11. Edit Customer Transaction
 * PUT /api/khatabook/customers/transaction/{transactionId}
 * Body: same as 1.10
 */
export const editCustomerTransaction = async (
  token,
  transactionId,
  { amount, type, note, transactionDate, khataBookMode }
) => {
  const response = await axios.put(
    `${BASE_URL}/api/khatabook/customers/transaction/${transactionId}`,
    {
      amount: Number(amount),
      type,
      note: note ? String(note).trim() : "",
      transactionDate,
      khataBookMode,
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 1.12. Delete Customer Transaction
 * DELETE /api/khatabook/customers/transaction/{transactionId}
 */
export const deleteCustomerTransaction = async (token, transactionId) => {
  const response = await axios.delete(
    `${BASE_URL}/api/khatabook/customers/transaction/${transactionId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/* =========================================================================
   2. CASHBOOK TOOL APIS
   ========================================================================= */

/**
 * 2.1. List All Cashbooks
 * POST /api/cashbook/all
 * Body: { "page_number": 1, "page_size": 1000, "order_by": "", "keyword_search": "" }
 */
export const listCashbooks = async (
  token,
  { page_number = 1, page_size = 1000, order_by = "", keyword_search = "" } = {}
) => {
  const response = await axios.post(
    `${BASE_URL}/api/cashbook/all`,
    {
      page_number,
      page_size,
      order_by,
      keyword_search,
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 2.2. Add a New Cashbook
 * POST /api/cashbook/add
 * Body: { "book_name": "Site Cashbook", "description": "Daily expenses for site" }
 * Response: { cashBook }
 */
export const addCashbook = async (token, { book_name, description }) => {
  const response = await axios.post(
    `${BASE_URL}/api/cashbook/add`,
    {
      book_name: String(book_name).trim(),
      description: description ? String(description).trim() : "",
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 2.3. Edit Cashbook
 * PUT /api/cashbook/edit
 * Body: { "id": 1, "book_name": "Updated Site Cashbook", "description": "Updated description" }
 */
export const editCashbook = async (token, { id, book_name, description }) => {
  const response = await axios.put(
    `${BASE_URL}/api/cashbook/edit`,
    {
      id: Number(id),
      book_name: String(book_name).trim(),
      description: description ? String(description).trim() : "",
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 2.4. Get Cashbook Details by ID
 * GET /api/cashbook/id/{cashbookId}
 */
export const getCashbookById = async (token, cashbookId) => {
  const response = await axios.get(
    `${BASE_URL}/api/cashbook/id/${cashbookId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 2.5. Delete Cashbook
 * DELETE /api/cashbook/delete/{cashbookId}
 */
export const deleteCashbook = async (token, cashbookId) => {
  const response = await axios.delete(
    `${BASE_URL}/api/cashbook/delete/${cashbookId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 2.6. Get Cashbook Entries
 * POST /api/cashbook-entry/cashbook/{cashbookId}
 * Body: { "page_number": 1, "page_size": 1000, "order_by": "" }
 */
export const getCashbookEntries = async (
  token,
  cashbookId,
  { page_number = 1, page_size = 1000, order_by = "" } = {}
) => {
  const response = await axios.post(
    `${BASE_URL}/api/cashbook-entry/cashbook/${cashbookId}`,
    {
      page_number,
      page_size,
      order_by,
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 2.7. Add Entry to Cashbook
 * POST /api/cashbook-entry/add
 * Body: { "cashbook_id": 1, "entry_type": "CREDIT", "amount": 2500 }
 */
export const addCashbookEntry = async (
  token,
  { cashbook_id, entry_type, amount }
) => {
  const response = await axios.post(
    `${BASE_URL}/api/cashbook-entry/add`,
    {
      cashbook_id: Number(cashbook_id),
      entry_type,
      amount: Number(amount),
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 2.8. Edit Cashbook Entry
 * PUT /api/cashbook-entry/edit
 * Body: { "id": 10, "cashbook_id": 1, "entry_type": "DEBIT", "amount": 3000 }
 */
export const editCashbookEntry = async (
  token,
  { id, cashbook_id, entry_type, amount }
) => {
  const response = await axios.put(
    `${BASE_URL}/api/cashbook-entry/edit`,
    {
      id: Number(id),
      cashbook_id: Number(cashbook_id),
      entry_type,
      amount: Number(amount),
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 2.9. Delete Cashbook Entry
 * DELETE /api/cashbook-entry/delete/{entryId}
 */
export const deleteCashbookEntry = async (token, entryId) => {
  const response = await axios.delete(
    `${BASE_URL}/api/cashbook-entry/delete/${entryId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/* =========================================================================
   3. LEDGER & PROJECT MANAGEMENT TOOL APIS
   ========================================================================= */

/**
 * 3.1. List Projects (Partner / Customer)
 * POST /api/project/partner
 * Body: { "page_number": 1, "page_size": 10000 }
 */
export const listProjects = async (
  token,
  { page_number = 1, page_size = 10000 } = {}
) => {
  const response = await axios.post(
    `${BASE_URL}/api/project/partner`,
    {
      page_number,
      page_size,
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 3.2. Add a New Project
 * POST /api/project/add
 * Body: { "project_name", "client_name" (both required), "client_mobile", "client_address", "description" }
 * Response: { project }
 */
export const addProject = async (
  token,
  { project_name, client_name, client_mobile, client_address, description }
) => {
  const response = await axios.post(
    `${BASE_URL}/api/project/add`,
    {
      project_name: String(project_name).trim(),
      client_name: String(client_name).trim(),
      client_mobile: client_mobile ? String(client_mobile).trim() : "",
      client_address: client_address ? String(client_address).trim() : "",
      description: description ? String(description).trim() : "",
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 3.3. Edit Project
 * PUT /api/project/edit
 * Body: same as 3.2 plus "id"
 */
export const editProject = async (
  token,
  { id, project_name, client_name, client_mobile, client_address, description }
) => {
  const response = await axios.put(
    `${BASE_URL}/api/project/edit`,
    {
      id: Number(id),
      project_name: String(project_name).trim(),
      client_name: String(client_name).trim(),
      client_mobile: client_mobile ? String(client_mobile).trim() : "",
      client_address: client_address ? String(client_address).trim() : "",
      description: description ? String(description).trim() : "",
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 3.4. Delete Project
 * DELETE /api/project/delete/{projectId}
 */
export const deleteProject = async (token, projectId) => {
  const response = await axios.delete(
    `${BASE_URL}/api/project/delete/${projectId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 3.5. Get Overall Projects Ledger Summary
 * GET /api/project/ledger/summary
 */
export const getOverallProjectsLedgerSummary = async (token) => {
  const response = await axios.get(
    `${BASE_URL}/api/project/ledger/summary`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 3.6. Get Project Details by ID
 * GET /api/project/id/{projectId}
 */
export const getProjectById = async (token, projectId) => {
  const response = await axios.get(
    `${BASE_URL}/api/project/id/${projectId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 3.7. Get Project Ledger Summary (by Project ID)
 * GET /api/project/ledger/summary/{projectId}
 */
export const getProjectLedgerSummary = async (token, projectId) => {
  const response = await axios.get(
    `${BASE_URL}/api/project/ledger/summary/${projectId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 3.8. Get Project Ledgers List
 * POST /api/project/ledger/{projectId}
 * Body: { "page_number": 1, "page_size": 1000 } (1-based pages)
 * Response: { projectLedgers: [{ id, type, amount, transaction_date "dd-MM-yyyy HH:mm:ss", description, reference_number, balance }] }
 */
export const getProjectLedgersList = async (token, projectId, { page_number = 1, page_size = 1000 } = {}) => {
  const response = await axios.post(
    `${BASE_URL}/api/project/ledger/${projectId}`,
    { page_number, page_size },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 3.9. Add Project Ledger Entry
 * POST /api/project/ledger/add
 * Body: {
 *   "project_id": 5, "type": "CREDIT" | "DEBIT", "amount": 25000,
 *   "transaction_date": "12-09-2026 10:30:00", "description": "Client advance payment"
 * }
 */
export const addProjectLedgerEntry = async (
  token,
  { project_id, type, amount, transaction_date, description }
) => {
  const response = await axios.post(
    `${BASE_URL}/api/project/ledger/add`,
    {
      project_id: Number(project_id),
      type,
      amount: Number(amount),
      transaction_date,
      description: description ? String(description).trim() : "",
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 3.10. Edit Project Ledger Entry
 * PUT /api/project/ledger/edit
 * Body: same as 3.9 plus "id"
 */
export const editProjectLedgerEntry = async (
  token,
  { id, project_id, type, amount, transaction_date, description }
) => {
  const response = await axios.put(
    `${BASE_URL}/api/project/ledger/edit`,
    {
      id: Number(id),
      project_id: Number(project_id),
      type,
      amount: Number(amount),
      transaction_date,
      description: description ? String(description).trim() : "",
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 3.11. Delete Project Ledger Entry
 * DELETE /api/project/ledger/delete/{ledgerId}
 */
export const deleteProjectLedgerEntry = async (token, ledgerId) => {
  const response = await axios.delete(
    `${BASE_URL}/api/project/ledger/delete/${ledgerId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 3.12. Get Project Notes
 * GET /api/project/note/{projectId}
 * Response: { projectNotes: [{ id, title, content, updated_date }] }
 */
export const getProjectNotes = async (token, projectId) => {
  const response = await axios.get(
    `${BASE_URL}/api/project/note/${projectId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 3.13. Add Project Note
 * POST /api/project/note/add
 * Body: { "project_id": 5, "title": "Kitchen tiles", "content": "Client requested changes" } (title and content required)
 * Response: { projectNote }
 */
export const addProjectNote = async (token, { project_id, title, content }) => {
  const response = await axios.post(
    `${BASE_URL}/api/project/note/add`,
    {
      project_id: Number(project_id),
      title: String(title).trim(),
      content: String(content).trim(),
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 3.14. Edit Project Note
 * PUT /api/project/note/edit
 * Body: same as 3.13 plus "id"
 */
export const editProjectNote = async (token, { id, project_id, title, content }) => {
  const response = await axios.put(
    `${BASE_URL}/api/project/note/edit`,
    {
      id: Number(id),
      project_id: Number(project_id),
      title: String(title).trim(),
      content: String(content).trim(),
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 3.15. Delete Project Note
 * DELETE /api/project/note/delete/{noteId}
 */
export const deleteProjectNote = async (token, noteId) => {
  const response = await axios.delete(
    `${BASE_URL}/api/project/note/delete/${noteId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/* =========================================================================
   6. LEASE MANAGEMENT TOOL APIS
   ========================================================================= */

/**
 * 6.1. List All Leases
 * POST /lease/leases
 * Body: { "page_number": 1, "page_size": 20, "order_by": "DESC" }
 */
export const listLeases = async (
  token,
  { page_number = 1, page_size = 20, order_by = "DESC" } = {}
) => {
  const response = await axios.post(
    `${BASE_URL}/lease/leases`,
    {
      page_number,
      page_size,
      order_by,
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 6.2. Add New Lease
 * POST /lease/add
 * Body: {
 *   "client_name": "Rohan Gupta",
 *   "client_mobile": "9876543210",
 *   "property_name": "Skyline Heights 402",
 *   "rent_amount": 35000,
 *   "deposit_amount": 70000,
 *   "start_date": "2026-10-01",
 *   "end_date": "2027-09-30"
 * }
 */
export const addLease = async (token, leaseData) => {
  const response = await axios.post(
    `${BASE_URL}/lease/add`,
    {
      client_name: String(leaseData.client_name || "").trim(),
      client_mobile: String(leaseData.client_mobile || "").trim(),
      property_name: String(leaseData.property_name || "").trim(),
      rent_amount: Number(leaseData.rent_amount) || 0,
      deposit_amount: Number(leaseData.deposit_amount) || 0,
      start_date: leaseData.start_date || "",
      end_date: leaseData.end_date || "",
      notes: leaseData.notes ? String(leaseData.notes).trim() : undefined,
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 6.3. Edit Lease
 * POST /lease/edit
 * Body: {
 *   "id": 8,
 *   "client_name": "Rohan Gupta",
 *   "client_mobile": "9876543210",
 *   "rent_amount": 36000
 * }
 */
export const editLease = async (token, leaseData) => {
  const response = await axios.post(
    `${BASE_URL}/lease/edit`,
    {
      ...leaseData,
      id: Number(leaseData.id),
      rent_amount: leaseData.rent_amount !== undefined ? Number(leaseData.rent_amount) : undefined,
      deposit_amount: leaseData.deposit_amount !== undefined ? Number(leaseData.deposit_amount) : undefined,
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 6.4. Delete Lease
 * DELETE /lease/delete/{leaseId}
 */
export const deleteLease = async (token, leaseId) => {
  const response = await axios.delete(
    `${BASE_URL}/lease/delete/${leaseId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/**
 * 6.5. Get Lease Notes
 * POST /lease/notes
 * Body: { "lead_id": 8, "page_number": 1, "page_size": 50 }
 */
export const getLeaseNotes = async (
  token,
  { lead_id, page_number = 1, page_size = 50 }
) => {
  const response = await axios.post(
    `${BASE_URL}/lease/notes`,
    {
      lead_id: Number(lead_id),
      page_number,
      page_size,
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};
